import React from 'react';

import classNames from 'classnames';
import Select, { IndicatorProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { OptionProps } from 'react-select/src/components/Option';

import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

// https://react-select.com/components

export const NoOptionsMessage = (props: OptionProps<any>) => (
  <div className="u-padding-y u-padding-x-xl u-color-gray" {...props.innerProps}>
    {props.children}
  </div>
);

export const inputComponent = (props: OptionProps<any> & { inputRef: React.Ref<any> }) => {
  const { inputRef, ...newProps } = props;
  return <div ref={newProps.innerRef} {...newProps} />;
};

export const Control = (props: OptionProps<any>) => (
  <TextField
    fullWidth
    InputProps={{
      inputComponent,
      inputProps: {
        className: 'u-display-flex u-padding-none u-height-100 u-minHeight-50px',
        children: props.children,
        ...props.innerProps,
      },
    }}
    {...props.selectProps.textFieldProps}
  />
);

export const Option = (props: OptionProps<any>) => (
  <MenuItem ref={props.innerRef} selected={props.isFocused} component="div" {...props.innerProps}>
    {props.children}
  </MenuItem>
);

export const Placeholder = (props: OptionProps<any>) => (
  <div
    color="textSecondary"
    className="u-color-gray u-position-absolute"
    style={{ left: 2 }}
    {...props.innerProps}
  >
    {props.children}
  </div>
);

export const SingleValue = (props: OptionProps<any>) => (
  <div {...props.innerProps}>{props.children}</div>
);

export const ValueContainer = (props: OptionProps<any>) => (
  <div className=" u-flex-center u-flexWrap-wrap u-flex-1 u-overflowY-scroll u-height-100">
    {props.children}
  </div>
);

export const MultiValue = (props: any) => (
  <Chip
    tabIndex={-1}
    variant="outlined"
    label={props.children}
    className={classNames('u-margin-y-sm u-margin-x-xs u-border-neutral10 u-flex-center', {
      'u-background-lightGray': props.isFocused,
    })}
    onDelete={props.removeProps.onClick}
    deleteIcon={
      <MaterialDesignIcon
        iconName={ICON_NAMES.CLOSE}
        size="xs"
        style={{ marginTop: 6 }}
        {...props.removeProps}
      />
    }
  />
);

export const Menu = (props: OptionProps<any>) => (
  <Paper
    square
    className="u-position-absolute u-zIndex-1 u-margin-top"
    style={{ left: 0, right: 0 }}
    {...props.innerProps}
  >
    {props.children}
  </Paper>
);

export const ClearIndicator = (props: IndicatorProps<any>) => (
  <div
    style={props.getStyles('clearIndicator', props)}
    {...props.innerProps}
    className="u-colorHover-secondary"
  >
    <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} />
  </div>
);

export const DropdownIndicator = (props: IndicatorProps<any>) => (
  <div
    style={props.getStyles('dropdownIndicator', props)}
    {...props.innerProps}
    className="u-colorHover-secondary"
  >
    <MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />
  </div>
);

const components: any = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  ClearIndicator,
  DropdownIndicator,
};

export type ReactSelectorValue = {
  label: string;
  value: string;
};

export type ChipSelectProps = {
  options: Array<ReactSelectorValue>;
  value: ReactSelectorValue | Array<ReactSelectorValue> | null;
  onChange: (value: any, actionMeta: any) => void;
  label?: string;
  placeholder?: string;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  isValidNewOption?: (inputValue: any, selectValue: any, selectOptions: any) => boolean;
  autofocus?: boolean;
};

export const chipSelectStyles = {
  input: (base: any) => ({
    ...base,
    '& input': {
      font: 'inherit',
    },
  }),
  clearIndicator: (base: any) => ({
    ...base,
    cursor: 'pointer',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    cursor: 'pointer',
  }),
};

export const UiChipSelect = (props: ChipSelectProps) => {
  const { options, value, onChange, label, placeholder, autofocus } = props;

  return (
    <div className="u-flexGrow-1" data-cy="custom-selector">
      <Select
        styles={chipSelectStyles}
        textFieldProps={{
          label,
          InputLabelProps: {
            shrink: true,
          },
        }}
        options={options}
        components={components}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti
        ref={(input) => {
          if (input && autofocus) {
            input.focus();
          }
        }}
      />
    </div>
  );
};

export const UiCreatableChipSelect = (props: ChipSelectProps) => {
  const {
    options,
    value,
    onChange,
    label,
    placeholder,
    formatCreateLabel,
    isValidNewOption,
    autofocus,
  } = props;

  return (
    <div className="u-flexGrow-1">
      <CreatableSelect
        allowCreateWhileLoading={false}
        styles={chipSelectStyles}
        textFieldProps={{
          label,
          InputLabelProps: {
            shrink: true,
          },
        }}
        options={options}
        components={components}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti
        formatCreateLabel={formatCreateLabel}
        isValidNewOption={isValidNewOption}
        ref={(input) => {
          if (input && autofocus) {
            input.focus();
          }
        }}
      />
    </div>
  );
};
